function msToDays(ms) {
  const seconds = Math.floor(ms / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);
  return days;
}
export function getDiffInDays(d1, d2) {
  return msToDays(d1.getTime() - d2.getTime());
}
export function removeTime(date) {
  return new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0));
}
export function addDays(date, days) {
  const newDate = new Date(date);
  newDate.setDate(newDate.getDate() + days);
  return newDate;
}

/**
 * Adding hours
 * @param date - A Date object
 * @param  hours - Number of hours to add
 * @returns The Date object after adding the value
 */
export function addHours(date) {
  let hours = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 0;
  return new Date(date.getTime() + hours * 60 * 60 * 1000);
}

/**
 * Adding minutes
 * @param date - A Date object
 * @param minutes - Number of minutes to add
 * @returns The Date object after adding the value
 */
export function addMinutes(date, minutes) {
  return new Date(date.getTime() + minutes * 60 * 1000);
}
export function getTimestamp() {
  return String(Date.now());
}
export function isValidDate(d) {
  return !isNaN(d.getTime());
}

/**
 * Returns the start of the hour for the given date.
 *
 * This function creates a new Date object with the minutes, seconds, and milliseconds
 * set to 0, effectively returning the beginning of the hour that contains the input date.
 *
 * @param date - The date to get the start of hour for
 * @returns A new Date object representing the start of the hour
 *
 * @example
 * ```ts
 * const date = new Date('2023-12-25T14:30:45.123Z');
 * const hourStart = startOfHour(date);
 * // Returns: 2023-12-25T14:00:00.000Z
 * ```
 */
export function startOfHour(date) {
  const _date = new Date(date);
  _date.setMinutes(0, 0, 0);
  return _date;
}

/**
 * Generates an array of time interval strings within a specified time range.
 *
 * This function creates time slots at regular intervals between a start and end hour.
 * Each time slot is formatted as "HH:MM" in 24-hour format. Useful for creating
 * appointment booking time slots or scheduling interfaces.
 *
 * @param startHour - The starting hour (0-24, defaults to 0)
 * @param endHour - The ending hour (must be between startHour and 24, defaults to 24)
 * @param intervalMinutes - The interval between time slots in minutes (defaults to 30)
 * @returns Array of time strings formatted as "HH:MM"
 * @throws Error if startHour is not between 0 and 24
 * @throws Error if endHour is greater than 24 or less than startHour
 * @throws Error if intervalMinutes is greater than 60
 *
 * @example
 * ```ts
 * // Generate 30-minute intervals from 9 AM to 5 PM
 * const slots = generateTimeIntervals(9, 17, 30);
 * // Returns: ["09:00", "09:30", "10:00", "10:30", ...]
 * ```
 */
export function generateTimeIntervals() {
  let startHour = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : 0;
  let endHour = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 24;
  let intervalMinutes = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : 30;
  const minutesInHour = 60;
  const slots = [];
  const startMinutes = startHour * minutesInHour;
  const endMinutes = endHour * minutesInHour;
  if (startHour < 0 || startHour > 24) {
    throw new Error('Start hour must be a value between 0 and 24');
  }
  if (endHour > 24 || endHour < startHour) {
    throw new Error('End hour must be a value beetween `startHour` and 24');
  }
  if (intervalMinutes <= 0) {
    throw new Error('intervalMinutes must be greater than 0');
  }
  const timeRange = (endHour - startHour) * minutesInHour;
  if (timeRange > 0 && intervalMinutes > timeRange) {
    throw new Error('intervalMinutes cannot be larger than the time range');
  }
  for (let minutes = startMinutes; minutes < endMinutes; minutes += intervalMinutes) {
    const hour = Math.floor(minutes / minutesInHour);
    const minute = minutes % minutesInHour;
    const formattedHour = hour.toString().padStart(2, '0');
    const formattedMinute = minute.toString().padStart(2, '0');
    slots.push(`${formattedHour}:${formattedMinute}`);
  }
  return slots;
}
/**
 * Rounds the minutes of a given date up to the next specified interval.
 *
 * This function takes a date and rounds its minutes up to the nearest interval boundary.
 * Seconds and milliseconds are always reset to 0. If the date is already at an exact
 * interval boundary with no seconds or milliseconds, it returns the date unchanged.
 *
 * @param date - The date to round up (must be a valid Date object)
 * @param interval - The minute interval to round up to (must be > 0, negative values become positive)
 * @returns A new Date object with minutes rounded up to the next interval
 *
 * @example
 * ```ts
 * const date = new Date('2023-12-25T14:23:45.123Z');
 * const rounded = roundMinutesUp(date, 15);
 * // Returns: 2023-12-25T14:30:00.000Z
 * ```
 *
 * @example
 * ```ts
 * const exactDate = new Date('2023-12-25T14:30:00.000Z');
 * const rounded = roundMinutesUp(exactDate, 15);
 * // Returns: 2023-12-25T14:30:00.000Z (unchanged)
 * ```
 *
 * @throws {Error} When interval is <= 0 or > 60
 *
 */
export function roundMinutesUp(date, interval) {
  const newDate = new Date(date);
  const minutes = newDate.getMinutes();
  const seconds = newDate.getSeconds();
  const milliseconds = newDate.getMilliseconds();
  const parsedInterval = Math.abs(Math.floor(interval));
  if (parsedInterval <= 0 || parsedInterval > 60) {
    throw new Error('Interval must be between 1 and 60');
  }

  // If already at exact interval and no seconds/milliseconds, don't round up
  if (minutes % parsedInterval === 0 && seconds === 0 && milliseconds === 0) {
    return newDate;
  }

  // Otherwise, we calculate the rounded minutes and add 1 interval if needed.
  const intervalsPassed = Math.floor(minutes / parsedInterval);
  let roundedMinutes = (intervalsPassed + 1) * parsedInterval;

  // Set the new minutes, and zero out seconds/milliseconds
  newDate.setMinutes(roundedMinutes, 0, 0);
  return newDate;
}